import { CommonModule, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { provideNativeDateAdapter } from '@angular/material/core';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';

export type InputSize = 'sm' | 'md' | 'lg';
export type InputVariant =
  | 'default'
  | 'primary'
  | 'error'
  | 'success'
  | 'warning'
  | 'info';

@Component({
  selector: 'app-date-range-picker',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './date-range-picker.component.html',
  styleUrls: ['./date-range-picker.component.scss'],
  providers: [
    provideNativeDateAdapter(),
    DatePipe,
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangePickerComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DateRangePickerComponent implements ControlValueAccessor, OnInit {
  @ViewChild('picker') picker!: MatDatepicker<Date>;

  @Input() label?: string;
  @Input() placeholder: string = 'Seleccione un rango';
  @Input() helperText: string = '';
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() id?: string;
  @Input() help?: string;
  @Input() startIcon?: string;
  @Input() endIcon: string = 'calendar_today';

  @Input() variant: InputVariant = 'default';
  @Input() size: InputSize = 'md';

  @Input() control?: AbstractControl;
  @Input() errorMessages?: string;

  public dateControl: FormControl<Date | null>;
  public displayValue: string = '';
  public focused: boolean = false;
  public inputId: string = `input-${Math.random().toString(36).substring(2, 9)}`;

  private onTouched: () => void = () => {};
  private onChange: (value: Date | null) => void = () => {};

  constructor(
    private readonly fb: FormBuilder,
    private readonly cdr: ChangeDetectorRef,
    private readonly datePipe: DatePipe,
  ) {
    this.dateControl = this.fb.control<Date | null>(null);
  }

  ngOnInit(): void {
    // Si se pasa un control externo, sincronizarlo con el interno
    if (this.control) {
      this.dateControl = this.control as FormControl;
    }
    // debug logs removed
    this.dateControl.valueChanges.subscribe((value) => {
      this.onChange(value);
      this.updateDisplayValue(value);
    });
    // Forzar actualización visual del valor inicial
    this.updateDisplayValue(this.dateControl.value);
  }

  writeValue(value: Date | null): void {
    if (value) {
      const d = new Date(value);
      const normalized = new Date(
        d.getUTCFullYear(),
        d.getUTCMonth(),
        d.getUTCDate(),
      );
      this.dateControl.setValue(normalized, { emitEvent: false });
      this.updateDisplayValue(normalized);
    } else {
      this.dateControl.setValue(null, { emitEvent: false });
      this.updateDisplayValue(null);
    }
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: Date | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.dateControl.disable();
    } else {
      this.dateControl.enable();
    }
    this.cdr.markForCheck();
  }

  private updateDisplayValue(value: Date | null): void {
    if (value) {
      this.displayValue = this.datePipe.transform(value, 'dd/MM/yyyy') ?? '';
    } else {
      this.displayValue = '';
    }
    this.cdr.markForCheck();
  }

  openPicker(): void {
    if (!this.disabled) {
      this.picker.open();
    }
  }

  onFocus(): void {
    if (!this.disabled) {
      this.focused = true;
    }
  }

  onBlur(): void {
    this.focused = false;
    this.onTouched();
  }

  clearValue(event: MouseEvent): void {
    event.stopPropagation();
    if (!this.disabled) {
      this.dateControl.setValue(null);
      this.onChange(null);
      this.onTouched();
      this.displayValue = '';
      this.cdr.markForCheck();
    }
  }

  get hasError(): boolean {
    if (this.control) {
      return (
        this.control.invalid && (this.control.touched || this.control.dirty)
      );
    }
    return Boolean(this.errorMessages && this.errorMessages.trim().length > 0);
  }

  get currentVariant(): InputVariant {
    return this.hasError ? 'error' : this.variant;
  }

  get variantClasses(): Record<InputVariant, string> {
    return {
      default: 'border-secondary',
      primary: 'border-primary',
      error: 'border-destructive',
      success: 'border-success',
      warning: 'border-warning',
      info: 'border-info',
    };
  }

  get sizeClasses(): Record<InputSize, string> {
    return {
      sm: 'text-xs h-6 px-2',
      md: 'text-sm h-8 px-2',
      lg: 'text-base h-10 px-3',
    };
  }

  get iconSizeClasses(): Record<InputSize, string> {
    return {
      sm: 'text-base',
      md: 'text-lg',
      lg: 'text-xl',
    };
  }

  get containerClasses(): string {
    const base =
      'flex items-center border-2 rounded-md transition-all duration-150 ease-in-out bg-background-light';
    const focus = this.focused ? 'ring-opacity-50' : '';
    const variant = this.variantClasses[this.currentVariant];
    const disabled = this.disabled
      ? 'opacity-50 cursor-not-allowed bg-background-extralight'
      : 'cursor-pointer';
    return [base, variant, focus, disabled].filter(Boolean).join(' ');
  }

  get inputDisplayClasses(): string {
    const base =
      'bg-transparent outline-none w-full select-none flex items-center';
    const size = this.sizeClasses[this.size];
    const placeholderColor = this.displayValue ? 'text-white' : 'text-gray-400';
    return [base, size, placeholderColor].filter(Boolean).join(' ');
  }

  get labelClasses(): string {
    const base = 'block font-medium mb-1';
    const size = this.size === 'lg' ? 'text-sm' : 'text-xs';
    return `${base} ${size}`;
  }

  get iconClasses(): string {
    const size = this.iconSizeClasses[this.size];
    return `text-gray-400 flex-shrink-0 ${size}`;
  }

  get errorTextClasses(): string {
    return 'text-xs text-destructive mt-1';
  }

  get helperTextClasses(): string {
    return 'text-xs text-secondary mt-1';
  }
}
